import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { authService } from '../../services/authService';
import { useTheme } from '../../contexts/ThemeContext';
import { TrustedDevice } from '../../types/auth';

/**
 * Lets the signed-in user view and revoke their trusted devices (the "don't ask
 * for an OTP again" entries). Works for any authenticated user.
 */
export default function TrustedDevicesSettings() {
  const { theme } = useTheme();
  const [devices, setDevices] = useState<TrustedDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await authService.getTrustedDevices();
      setDevices((res?.devices || []) as TrustedDevice[]);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load devices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const revoke = async (id: string) => {
    setBusy(id);
    try {
      await authService.revokeTrustedDevice(id);
      setDevices((d) => d.filter((x) => x.id !== id));
      toast.success('Device removed');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to remove device');
    } finally {
      setBusy(null);
    }
  };

  const revokeAll = async () => {
    setBusy('all');
    try {
      await authService.revokeAllTrustedDevices();
      setDevices([]);
      toast.success('All devices removed');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to remove devices');
    } finally {
      setBusy(null);
    }
  };

  const fmt = (s: string | null) => (s ? new Date(s).toLocaleString() : '—');

  return (
    <div className={theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold">Trusted devices</h2>
          <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            These devices can sign in without an email verification code. Remove any you don't
            recognize — that device will need a fresh code next time.
          </p>
        </div>
        {devices.length > 0 && (
          <button
            onClick={revokeAll}
            disabled={busy === 'all'}
            className="text-sm px-3 py-1.5 rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
          >
            {busy === 'all' ? 'Removing…' : 'Remove all'}
          </button>
        )}
      </div>

      {loading ? (
        <p className="text-sm">Loading…</p>
      ) : devices.length === 0 ? (
        <p className="text-sm">
          No trusted devices. You'll be asked for a verification code on each sign-in.
        </p>
      ) : (
        <div className="space-y-2">
          {devices.map((d) => (
            <div
              key={d.id}
              className={`flex items-center justify-between p-3 rounded-md border ${
                theme === 'dark' ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
              }`}
            >
              <div className="text-sm">
                <div className="font-medium">{d.device_label || 'Unknown device'}</div>
                <div className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>
                  Last used {fmt(d.last_used_at)} · IP {d.last_used_ip || d.created_ip || '—'} ·
                  Expires {fmt(d.expires_at)}
                </div>
              </div>
              <button
                onClick={() => revoke(d.id)}
                disabled={busy === d.id}
                className={`text-sm px-3 py-1.5 rounded-md disabled:opacity-50 ${
                  theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
                }`}
              >
                {busy === d.id ? 'Removing…' : 'Remove'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
